import api from '@/shared/api/api';
import { BASE_URI } from '@/shared/config/config';

import {
  encounterPreviewToDomain,
  encounterToDomain,
} from './scribeEncounterConv';
import { callScribe } from './scribeError';
import {
  documentAnswerChangeToWire,
  documentAnswersToDomain,
  liveSessionToDomain,
  liveTranscriptToWire,
  partnerExportToDomain,
} from './scribeLiveConv';
import {
  TranscriptorApiError,
  transcriptorErrorCode,
} from './transcriptorError';
import type {
  DocumentAnswerChange,
  DocumentAnswers,
  DocumentSectionOverride,
  DocumentUpdateRequest,
  FinalDocumentRevision,
  EncounterRecording,
  LiveTranscriptionSession,
  NoteAnswersUpdate,
  PartnerDocumentExport,
  RecordingMode,
  RecordingPingTelemetry,
  RecordingStopReason,
  ReprocessRequestOptions,
  TranscriptorApiResponse,
  TranscriptorConsultationResponse,
  TranscriptorEncounterListResponse,
  TranscriptChunk,
  TranscriptorLanguage,
  UsedEncounterSchema,
} from './transcriptorTypes';

// Recording and upload still speak REST: the contract has no RPCs for the
// pipeline behind them.
const TRANSCRIPTOR_API_BASE = `${BASE_URI}/api/scribe`;

// Scribe stores the browser's zone on a block note the first time it sees
// one and dates the visit in it; core has no clinic time zone.
const getClientTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;

const NO_NOTE_UPDATE: Required<NoteAnswersUpdate> = {
  Changes: [],
  LineChanges: [],
  SectionChanges: [],
  AddLines: [],
  AddedBlockChanges: [],
};

const mergeNoteUpdates = (updates: NoteAnswersUpdate[]) =>
  updates.reduce<Required<NoteAnswersUpdate>>(
    (merged, update) => ({
      Changes: [...merged.Changes, ...(update.Changes ?? [])],
      LineChanges: [...merged.LineChanges, ...(update.LineChanges ?? [])],
      SectionChanges: [
        ...merged.SectionChanges,
        ...(update.SectionChanges ?? []),
      ],
      AddLines: [...merged.AddLines, ...(update.AddLines ?? [])],
      AddedBlockChanges: [
        ...merged.AddedBlockChanges,
        ...(update.AddedBlockChanges ?? []),
      ],
    }),
    NO_NOTE_UPDATE,
  );

export * from './transcriptorTypes';
export {
  isFollowUpDocument,
  isLiteratureReviewDocument,
} from './scribeEncounterConv';
export { noteCompletionToDomain } from './scribeLiveConv';

export {
  getDictationRefusal,
  getPartnerExportRefusal,
  getScribeErrorText,
  getTemplateIssues,
  isStaleAnswersRevisionError,
  isTemplateSeriesBusyError,
  isTemplateSeriesMovedError,
  ScribeError,
} from './scribeError';

export { noteListsApi, type NoteListInput } from './noteListsApi';

export {
  SCRIBE_TRANSCRIPTION_DISABLED,
  TRANSCRIPTION_DISABLED_CODE,
  TranscriptorApiError,
} from './transcriptorError';

// API client class
export class TranscriptorApiClient {
  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<TranscriptorApiResponse<T>> {
    const url = `${TRANSCRIPTOR_API_BASE}${path}`;

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Include cookies for auth
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }

        throw new TranscriptorApiError(
          errorMessage,
          transcriptorErrorCode(response.status, errorMessage),
          undefined,
          response.status,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof TranscriptorApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  // For endpoints that return data directly without { data, error, meta } wrapper
  private async requestDirect<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${TRANSCRIPTOR_API_BASE}${path}`;

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = `${errorMessage} - ${errorText}`;
        }

        throw new TranscriptorApiError(
          errorMessage,
          transcriptorErrorCode(response.status, errorMessage),
          undefined,
          response.status,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  // Encounter schemas that actually appear in the organization's encounters,
  // with their counts — unlike the catalog listing, a schema nobody used is
  // absent here.
  async getUsedEncounterSchemas(params: {
    organization_id: string;
    include_archived?: boolean;
  }): Promise<UsedEncounterSchema[]> {
    const response = await callScribe(
      api.encounterSchema.listUsage({
        OrganizationID: params.organization_id,
        IncludeArchived: params.include_archived,
      }),
    );

    return response.Schemas.map((schema) => ({
      schema_code: schema.Code,
      schema_name: schema.Name,
      count: schema.Count,
    }));
  }

  async getRecordingCapabilities(
    organizationId: string,
  ): Promise<{ dictationEnabled: boolean }> {
    const response = await callScribe(
      api.encounter.getRecordingCapabilities({
        OrganizationID: organizationId,
      }),
    );
    return { dictationEnabled: response.DictationEnabled === true };
  }

  async getConsultations(params?: {
    patient_id?: string;
    organization_id?: string;
    schema_code?: string;
    page?: number;
    limit?: number;
    resolve_patient_info?: boolean;
    include_archived?: boolean;
  }): Promise<TranscriptorEncounterListResponse> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 50;

    const response = await callScribe(
      api.encounter.list({
        PatientID: params?.patient_id,
        OrganizationID: params?.organization_id,
        SchemaCode: params?.schema_code,
        ResolvePatientInfo: params?.resolve_patient_info,
        IncludeArchived: params?.include_archived,
        Offset: (page - 1) * limit,
        Limit: limit,
      }),
    );

    return {
      encounters: response.Encounters.map(encounterPreviewToDomain),
      total: response.Total,
      page,
      limit,
    };
  }

  async getConsultation(
    id: string,
    params?: { resolvePatientInfo?: boolean },
  ): Promise<TranscriptorConsultationResponse> {
    const response = await callScribe(
      api.encounter.get({
        EncounterID: id,
        ResolvePatientInfo: params?.resolvePatientInfo,
      }),
    );

    return encounterToDomain(response.Encounter);
  }

  // Get presigned URL for audio upload
  async getUploadUrl(data: {
    patient_id: string;
    schema_id?: string;
    lang: TranscriptorLanguage;
    file_name: string;
    file_size: number;
    content_type: string;
    external_id?: string;
    selected_on_demand_series_ids?: string[];
  }): Promise<{
    upload_url: string;
    consultation_id: string;
    fragment_id: string;
  }> {
    const schema_id = data.schema_id;

    const response = await this.requestDirect<{
      upload_url: string;
      encounter_id: string;
      fragment_id: string;
    }>('/encounters/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: data.patient_id,
        external_id: data.external_id,
        file_name: data.file_name,
        file_size: data.file_size,
        content_type: data.content_type,
        lang: data.lang,
        ...(schema_id ? { schema_id } : {}),
        ...(data.selected_on_demand_series_ids
          ? {
              selected_on_demand_series_ids: data.selected_on_demand_series_ids,
            }
          : {}),
      }),
    });

    return {
      upload_url: response.upload_url,
      consultation_id: response.encounter_id,
      fragment_id: response.fragment_id,
    };
  }

  async getFragmentUploadUrl(
    consultationId: string,
    data: {
      file_name: string;
      file_size: number;
      content_type: string;
    },
  ): Promise<{
    upload_url: string;
    consultation_id: string;
    fragment_id: string;
  }> {
    const response = await this.requestDirect<{
      upload_url: string;
      encounter_id: string;
      fragment_id: string;
    }>(`/encounters/${consultationId}/fragments/upload-url`, {
      method: 'POST',
      body: JSON.stringify({
        file_name: data.file_name,
        file_size: data.file_size,
        content_type: data.content_type,
      }),
    });

    return {
      upload_url: response.upload_url,
      consultation_id: response.encounter_id,
      fragment_id: response.fragment_id,
    };
  }

  // Confirm that file has been uploaded to storage
  async confirmUpload(data: {
    consultation_id: string;
    fragment_id: string;
  }): Promise<{ consultation_id: string; status: string }> {
    const response = await this.requestDirect<{
      encounter_id: string;
      status: string;
    }>('/encounters/confirm-upload', {
      method: 'POST',
      body: JSON.stringify({
        encounter_id: data.consultation_id,
        fragment_id: data.fragment_id,
      }),
    });

    return {
      consultation_id: response.encounter_id,
      status: response.status,
    };
  }

  // Start recording session (browser-style with pre-generated IDs)
  async startRecording(
    consultationId: string,
    fragmentId: string,
    data: {
      patient_id: string;
      schema_id?: string;
      lang: TranscriptorLanguage;
      selected_on_demand_series_ids?: string[];
      recording_mode?: RecordingMode;
    },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    const schema_id = data.schema_id;

    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/start`,
      {
        method: 'POST',
        body: JSON.stringify({
          patient_id: data.patient_id,
          lang: data.lang,
          ...(schema_id ? { schema_id } : {}),
          ...(data.selected_on_demand_series_ids
            ? {
                selected_on_demand_series_ids:
                  data.selected_on_demand_series_ids,
              }
            : {}),
          ...(data.recording_mode
            ? { recording_mode: data.recording_mode }
            : {}),
        }),
      },
    );
  }

  // Read accepted recording settings before opening the microphone.
  async getEncounterRecording(
    consultationId: string,
  ): Promise<EncounterRecording> {
    const response = await this.request<{ encounter: EncounterRecording }>(
      `/encounters/${consultationId}`,
    );

    return response.data.encounter;
  }

  // Add audio chunk
  async addAudioChunk(
    consultationId: string,
    fragmentId: string,
    chunk: {
      audio_base64: string;
      type: string;
      timestamp: string;
      index: number;
      last: boolean;
    },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/chunks`,
      {
        method: 'POST',
        body: JSON.stringify(chunk),
      },
    );
  }

  // Stop recording. Without a reason the backend records the stop as the user
  // pressing Finish.
  async stopRecording(
    consultationId: string,
    fragmentId: string,
    options?: {
      reason?: RecordingStopReason;
      final_document_revisions?: FinalDocumentRevision[];
    },
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/stop`,
      {
        method: 'POST',
        body: options ? JSON.stringify(options) : undefined,
      },
    );
  }

  // Activity ping to prevent server session timeout during recording. The
  // optional telemetry is logged by the backend to diagnose recordings that
  // stop on their own.
  async activityPing(
    consultationId: string,
    fragmentId: string,
    telemetry?: RecordingPingTelemetry,
  ): Promise<TranscriptorApiResponse<{ success: boolean }>> {
    return this.request<{ success: boolean }>(
      `/encounters/${consultationId}/fragments/${fragmentId}/ping`,
      {
        method: 'POST',
        body: telemetry ? JSON.stringify(telemetry) : undefined,
      },
    );
  }

  // Reprocess consultation
  async reprocessConsultation(
    consultationId: string,
    options?: ReprocessRequestOptions,
  ): Promise<TranscriptorApiResponse<Record<string, never>>> {
    return this.request<Record<string, never>>(
      `/encounters/${consultationId}/reprocess`,
      {
        method: 'POST',
        body: JSON.stringify(options || {}),
      },
    );
  }

  async updateConsultation(
    consultationId: string,
    updates: {
      doctor_user_id?: string;
      staff_member_id?: string;
      patient_id?: string;
    },
  ): Promise<void> {
    await callScribe(
      api.encounter.update({
        EncounterID: consultationId,
        UserID: updates.doctor_user_id,
        StaffMemberID: updates.staff_member_id,
        PatientID: updates.patient_id,
      }),
    );
  }

  async archiveConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.archive({ EncounterID: consultationId }));
  }

  async restoreConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.restore({ EncounterID: consultationId }));
  }

  async deleteConsultation(consultationId: string): Promise<void> {
    await callScribe(api.encounter.delete({ EncounterID: consultationId }));
  }

  async updateConsultationDocument(
    consultationId: string,
    documentId: string,
    payload: DocumentUpdateRequest,
  ): Promise<void> {
    await callScribe(
      api.encounter.updateDocument({
        EncounterID: consultationId,
        DocumentID: documentId,
        Sections: payload.sections?.map((section) => ({
          Code: section.code,
          Content: section.content ?? undefined,
        })),
        Approved: payload.approved,
      }),
    );
  }

  async createLiveTranscriptionSession(
    encounterId: string,
  ): Promise<LiveTranscriptionSession> {
    const response = await callScribe(
      api.encounter.createLiveTranscriptionSession({
        EncounterID: encounterId,
      }),
    );

    return liveSessionToDomain(response);
  }

  async fillProtocol(params: {
    encounterId: string;
    documentId: string;
    transcriptRevision: number;
    language: string;
    chunks: TranscriptChunk[];
  }): Promise<DocumentAnswers | null> {
    const response = await callScribe(
      api.encounter.fillProtocol({
        EncounterID: params.encounterId,
        DocumentID: params.documentId,
        TranscriptRevision: BigInt(params.transcriptRevision),
        Transcript: liveTranscriptToWire(params),
        ClientTimeZone: getClientTimeZone(),
      }),
    );

    return documentAnswersToDomain(response.Answers);
  }

  async getDocumentAnswers(params: {
    encounterId: string;
    documentId: string;
  }): Promise<DocumentAnswers | null> {
    const response = await callScribe(
      api.encounter.getDocumentAnswers({
        EncounterID: params.encounterId,
        DocumentID: params.documentId,
        ClientTimeZone: getClientTimeZone(),
      }),
    );

    return documentAnswersToDomain(response.Answers);
  }

  // A block note of a partner template as the partner's template text;
  // scribe refuses a document whose template has no partner source.
  async exportPartnerDocument(params: {
    encounterId: string;
    documentId: string;
    signal?: AbortSignal;
  }): Promise<PartnerDocumentExport> {
    const response = await callScribe(
      api.encounter.exportPartnerDocument(
        {
          EncounterID: params.encounterId,
          DocumentID: params.documentId,
        },
        { signal: params.signal },
      ),
    );

    return partnerExportToDomain(response);
  }

  // Legacy changes and block-note operations go in one request; scribe
  // applies the block-note lists in its own fixed order.
  async updateDocumentAnswers(params: {
    encounterId: string;
    documentId: string;
    baseRevision: number;
    changes: DocumentAnswerChange[];
    noteUpdates?: NoteAnswersUpdate[];
  }): Promise<DocumentAnswers | null> {
    const note = mergeNoteUpdates(params.noteUpdates ?? []);
    const response = await callScribe(
      api.encounter.updateDocumentAnswers({
        EncounterID: params.encounterId,
        DocumentID: params.documentId,
        BaseRevision: BigInt(params.baseRevision),
        ...note,
        Changes: [
          ...params.changes.map(documentAnswerChangeToWire),
          ...note.Changes,
        ],
        ClientTimeZone: getClientTimeZone(),
      }),
    );

    return documentAnswersToDomain(response.Answers);
  }

  // A summary's or an evaluation's section titles and AI instructions set
  // before the recording, for this encounter only: every section of the
  // version the document is pinned to, in its order.
  async setDocumentSections(params: {
    encounterId: string;
    documentId: string;
    templateId: string;
    sections: DocumentSectionOverride[];
  }): Promise<void> {
    await callScribe(
      api.encounter.setDocumentSections({
        EncounterID: params.encounterId,
        DocumentID: params.documentId,
        TemplateID: params.templateId,
        Sections: params.sections.map((section) => ({
          Code: section.code,
          Title: section.title,
          Instructions: section.instructions,
        })),
      }),
    );
  }
}

export const transcriptorApi = new TranscriptorApiClient();
